/**
 * The staff inbox. Requests waiting on a decision sit beside the school's
 * messages, because both are things somebody has to look at and clear.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, useQuery } from '../api.js';
import { useSession } from '../session.js';
import { Segmented, Tile, type TileColor } from '../controls.js';
import { Drawer, Pill, Spinner, useToast } from '../ui.js';
import {
  AlertIcon, CalendarIcon, CheckCircleIcon, CloseIcon, DocIcon,
  ComposeIcon, GraduationIcon, InboxIcon, PeopleIcon, ShieldIcon, WalletIcon,
} from '../icons.js';
import { Avatar } from '../avatar.js';
import { Compose } from './Compose.js';
import type { QueuedRequest } from './RequestQueue.js';
import type { Reservation } from '../../../shared/types.js';
import { useLive } from '../live.js';

interface Message {
  id: number; kind: string; title: string; body: string; href: string;
  read: boolean; createdAt: number; dayLabel: string;
  from: { id: number; name: string; avatarUpdatedAt: number | null } | null;
}

const LOOK: Record<string, { color: TileColor; Glyph: (p: { className?: string }) => JSX.Element }> = {
  confirmed: { color: 'green', Glyph: CheckCircleIcon },
  requested: { color: 'orange', Glyph: CalendarIcon },
  declined: { color: 'red', Glyph: CloseIcon },
  cancelled: { color: 'gray', Glyph: CloseIcon },
  document: { color: 'indigo', Glyph: DocIcon },
  billing: { color: 'blue', Glyph: WalletIcon },
  training: { color: 'purple', Glyph: GraduationIcon },
  safety: { color: 'red', Glyph: ShieldIcon },
  notice: { color: 'orange', Glyph: AlertIcon },
  message: { color: 'teal', Glyph: PeopleIcon },
};

export function Inbox({ onClose, onAssign, onChanged, openWith }: {
  onClose: () => void;
  /** Opens the booking form with this request loaded. */
  onAssign: (request: QueuedRequest) => void;
  onChanged: () => void;
  /** A person to open a conversation with, named from another screen. */
  openWith?: number | null;
}): JSX.Element {
  const { can, role } = useSession();
  const isStudent = role === 'student';
  const toast = useToast();
  const navigate = useNavigate();

  // The staff queue is everybody's requests and is refused to a student, so a
  // student reads their own from the portal instead. Same tab, same place to
  // look, different question being asked.
  const requests = useQuery<{ requests: QueuedRequest[]; cancellations: Reservation[] }>(
    isStudent ? null : '/schedule/requests',
  );
  const mine = useQuery<{ requests: PortalRequest[] }>(isStudent ? '/portal' : null);
  const messages = useQuery<{ messages: Message[]; unread: number }>('/inbox');

  useLive('notifications', messages.reload);
  useLive('schedule', () => { requests.reload(); mine.reload(); });

  const [tab, setTab] = useState<'requests' | 'messages' | 'notices'>(openWith ? 'messages' : 'requests');
  const threads = useQuery<{ threads: Thread[]; unread: number }>('/inbox/threads');
  useLive('messages', threads.reload);
  const chatUnread = threads.data?.unread ?? 0;
  const [busy, setBusy] = useState(0);
  const [composing, setComposing] = useState(false);

  const waiting = requests.data?.requests ?? [];
  const cancellations = requests.data?.cancellations ?? [];
  const unread = messages.data?.unread ?? 0;
  const pending = waiting.length + cancellations.length;

  const decide = async (r: QueuedRequest, status: 'confirmed' | 'declined') => {
    setBusy(r.id);
    try {
      await api.post(`/schedule/reservations/${r.id}/status`, {
        status,
        reason: status === 'declined' ? 'Declined from the inbox' : undefined,
      });
      toast.ok(status === 'confirmed' ? 'Request approved' : 'Request declined');
      requests.reload();
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.findings?.length) {
        toast.error('That request cannot be approved', err.findings.map((f) => f.title).join(' · '));
      } else {
        toast.error('Could not do that', err instanceof Error ? err.message : undefined);
      }
    } finally {
      setBusy(0);
    }
  };

  /** The instructor's answer to a cancellation asked for inside the notice window. */
  const answerCancellation = async (r: Reservation, approve: boolean) => {
    setBusy(r.id);
    try {
      await api.post(`/schedule/reservations/${r.id}/cancellation`, { approve });
      toast.ok(approve ? 'Lesson cancelled' : 'Lesson kept on the schedule');
      requests.reload();
      onChanged();
    } catch (err) {
      toast.error('Could not do that', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(0);
    }
  };

  const openMessage = async (m: Message) => {
    if (!m.read) { await api.post(`/inbox/${m.id}/read`); messages.reload(); onChanged(); }
    if (m.href && !m.href.startsWith('/portal')) { onClose(); navigate(m.href); }
  };

  const readAll = async () => {
    const { marked } = await api.post<{ marked: number }>('/inbox/read-all');
    toast.ok(marked ? `${marked} marked as read` : 'Nothing unread');
    messages.reload();
    onChanged();
  };

  // Group messages by the day label the server produced, newest first.
  const groups: { label: string; messages: Message[] }[] = [];
  for (const m of messages.data?.messages ?? []) {
    let g = groups.find((x) => x.label === m.dayLabel);
    if (!g) { g = { label: m.dayLabel, messages: [] }; groups.push(g); }
    g.messages.push(m);
  }

  return (
    <Drawer
      title="Inbox"
      subtitle={`${pending} request${pending === 1 ? '' : 's'} waiting · ${unread} unread`}
      onClose={onClose}
      footer={
        <>
          {tab === 'notices' && (can('training:grade') || can('student:manage')) ? (
            <button className="btn btn--primary" onClick={() => setComposing(true)}>New notice</button>
          ) : null}
          <span style={{ flex: 1 }} />
          {tab === 'messages' && <Conversations threads={threads} openWith={openWith} />}

      {tab === 'notices' && (
            <button className="btn" onClick={readAll} disabled={!unread}>Mark all read</button>
          )}
        </>
      }
    >
      <div style={{ padding: '16px 24px 0' }}>
        <Segmented
          grow
          value={tab}
          onChange={setTab}
          label="Inbox sections"
          options={[
            { value: 'requests', label: pending ? `Requests ${pending}` : 'Requests' },
            { value: 'messages', label: chatUnread ? `Messages ${chatUnread}` : 'Messages' },
            { value: 'notices', label: unread ? `Notices ${unread}` : 'Notices' },
          ]}
        />
      </div>

      {tab === 'requests' && isStudent && (
        <div style={{ padding: '4px 24px 20px' }}>
          {!mine.data && <Spinner />}
          {mine.data?.requests.length === 0 && (
            <div className="empty">
              <InboxIcon style={{ width: 30, height: 30, opacity: 0.4, marginBottom: 10 }} />
              <div>You have not asked for a lesson yet.</div>
            </div>
          )}
          {mine.data?.requests.map((r) => (
            <div key={r.id} className="reqrow reqrow--flush">
              <div className="reqrow__when">{r.when}</div>
              <div className="reqrow__what">{r.lesson}</div>
              <div className="reqrow__who muted">
                {r.instructor || 'Instructor not assigned yet'}
              </div>
              {/* Read-only: a student can see where a request has got to, and
                  asks the school to change it rather than changing it here.
                  Cancelling stays on the schedule, beside the lesson itself. */}
              {r.cancelRequestedAt
                ? <span className="pill pill--amber" style={{ flex: 'none' }}>Cancellation asked for</span>
                : <Pill>{r.status === 'requested' ? 'Sent · waiting on the school' : r.status}</Pill>}
            </div>
          ))}
        </div>
      )}

      {tab === 'requests' && !isStudent && (
        <>
          {!requests.data && <Spinner />}
          {requests.data && pending === 0 && (
            <div className="empty">
              <InboxIcon style={{ width: 30, height: 30, opacity: 0.4, marginBottom: 10 }} />
              <div>No requests waiting on a decision.</div>
            </div>
          )}

          {/* Cancellations asked for inside the notice window sit first: the
              aircraft and the day are already committed, so they are the ones
              with a deadline. */}
          <div className="request-cards">
            {cancellations.map((r) => (
              <div className="request-card" key={`x${r.id}`}>
                <div className="row" style={{ gap: 11, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                  <Tile color="red"><CloseIcon /></Tile>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 600 }}>
                      {r.studentName || 'Student'} asked to cancel
                    </span>
                    <span className="small muted" style={{ display: 'block' }}>
                      {r.lessonLabel || r.activityLabel || 'Session'}
                    </span>
                  </span>
                </div>

                <div className="stack" style={{ gap: 4, marginTop: 12, fontSize: 13 }}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="muted">When</span>
                    <strong>{dayLabel(r.startsAt)} {time(r.startsAt)}–{time(r.endsAt)}</strong>
                  </div>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="muted">Aircraft</span>
                    <strong>{r.aircraftTail || '—'}</strong>
                  </div>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="muted">Asked</span>
                    <strong>{r.cancelRequestedAt ? dayLabel(r.cancelRequestedAt) : '—'}</strong>
                  </div>
                </div>

                {r.cancelReason && (
                  <div style={{
                    marginTop: 10, padding: '9px 11px', borderRadius: 'var(--r-md)',
                    background: 'var(--fill-3)', fontSize: 12.5, textWrap: 'pretty',
                  }}>
                    “{r.cancelReason}”
                  </div>
                )}

                {can('reservation:approve') || can('reservation:cancel') ? (
                  <div className="row" style={{ gap: 8, marginTop: 12 }}>
                    <button className="btn btn--sm btn--tinted-danger" disabled={busy === r.id}
                      onClick={() => answerCancellation(r, true)}>
                      Approve cancellation
                    </button>
                    <button className="btn btn--sm btn--tinted" disabled={busy === r.id}
                      onClick={() => answerCancellation(r, false)}>
                      Keep the lesson
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
            {waiting.map((r) => {
              const missing = [r.needsInstructor && 'an instructor', r.needsAircraft && 'an aircraft']
                .filter(Boolean).join(' and ');
              const blockers = r.blockers ?? [];
              return (
                <div className="request-card" key={r.id}>
                  <div className="row" style={{ gap: 11, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                    <Tile color="orange"><CalendarIcon /></Tile>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 600 }}>
                        {r.studentName || r.notes || r.kind}
                      </span>
                      <span className="small muted" style={{ display: 'block' }}>
                        {r.lessonLabel || r.activityLabel || 'Session'}
                      </span>
                    </span>
                  </div>

                  <div className="stack" style={{ gap: 4, marginTop: 12, fontSize: 13 }}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="muted">When</span>
                      <strong>{dayLabel(r.startsAt)} {time(r.startsAt)}–{time(r.endsAt)}</strong>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="muted">Aircraft</span>
                      <strong>{r.aircraftTail}</strong>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="muted">Instructor</span>
                      <strong>{r.instructorName || '—'}</strong>
                    </div>
                  </div>

                  {missing && (
                    <div style={{
                      marginTop: 10, padding: '9px 11px', borderRadius: 'var(--r-md)',
                      background: 'var(--tint-orange)', color: 'var(--k-pending-fg)', fontSize: 12.5,
                    }}>
                      Needs {missing} before it can be approved.
                    </div>
                  )}

                  {blockers.map((f) => (
                    <div key={f.code} style={{
                      marginTop: 10, padding: '9px 11px', borderRadius: 'var(--r-md)',
                      background: 'var(--tint-red)',
                      color: 'color-mix(in srgb, var(--sys-red) 88%, var(--label-1))',
                      fontSize: 12.5, textWrap: 'pretty',
                    }}>
                      <strong style={{ display: 'block' }}>{f.title}</strong>
                      {f.body}
                    </div>
                  ))}

                  <div className="row" style={{ gap: 8, marginTop: 12 }}>
                    {can('reservation:create') && (
                      <button className="btn btn--sm btn--tinted" disabled={busy === r.id}
                        onClick={() => { onClose(); onAssign(r); }}>
                        {missing ? 'Assign' : 'Change'}
                      </button>
                    )}
                    {can('reservation:approve') && (
                      <>
                        <button
                          className="btn btn--sm btn--primary"
                          disabled={busy === r.id || !!missing || blockers.length > 0}
                          title={missing ? `Assign ${missing} first`
                            : blockers.length ? blockers.map((f) => f.title).join('; ') : undefined}
                          onClick={() => decide(r, 'confirmed')}>
                          Approve
                        </button>
                        <button className="btn btn--sm btn--tinted-danger" disabled={busy === r.id}
                          onClick={() => decide(r, 'declined')}>
                          Decline
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === 'notices' && (
        <>
          {!messages.data && <Spinner />}
          {messages.data && groups.length === 0 && (
            <div className="empty">
              <InboxIcon style={{ width: 30, height: 30, opacity: 0.4, marginBottom: 10 }} />
              <div>Nothing here.</div>
            </div>
          )}
          {groups.map((group) => (
            <div key={group.label}>
              <div className="pane__head">{group.label}</div>
              {group.messages.map((m) => {
                const look = LOOK[m.kind] ?? { color: 'gray' as TileColor, Glyph: InboxIcon };
                return (
                  <button key={m.id} className="list__row list__row--tiled"
                    style={{ alignItems: 'flex-start', paddingLeft: 24, paddingRight: 24 }}
                    onClick={() => openMessage(m)}>
                    <span style={{ position: 'relative', display: 'flex' }}>
                      <Tile color={look.color}><look.Glyph /></Tile>
                      {!m.read && (
                        <span aria-label="Unread" style={{
                          position: 'absolute', top: -2, right: -2, width: 10, height: 10,
                          borderRadius: 999, background: 'var(--sys-blue)',
                          boxShadow: '0 0 0 2px var(--bg-raised)',
                        }} />
                      )}
                    </span>
                    <span className="list__label">
                      <span className="list__title" style={{ fontWeight: m.read ? 500 : 700 }}>{m.title}</span>
                      <span className="list__sub" style={{ textWrap: 'pretty' }}>{m.body}</span>
                      {m.from && (
                        <span className="row" style={{ gap: 6, marginTop: 4 }}>
                          <Avatar userId={m.from.id} updatedAt={m.from.avatarUpdatedAt}
                            initials={m.from.name.split(' ').map((x) => x[0]).join('')} size={18} />
                          <span className="small muted">{m.from.name}</span>
                        </span>
                      )}
                    </span>
                    <span className="list__value">
                      {new Date(m.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </>
      )}
      {composing && (
        <Compose onClose={() => setComposing(false)} onSent={onChanged} />
      )}
    </Drawer>
  );
}

function time(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}
function dayLabel(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
}

export { Pill };

/** What a student asked for, as the portal already reports it. */
export interface PortalRequest {
  id: number; startsAt: number; when: string; lesson: string; instructor: string; status: string;
  withinNotice: boolean; cancelRequestedAt: number | null; cancelReason: string | null;
}

export interface Thread {
  withUser: { id: number; name: string; role: string; initials: string; avatarUpdatedAt: number | null };
  last: { body: string; at: number; mine: boolean };
  unread: number;
}

interface ChatMessage { id: number; body: string; at: number; mine: boolean; read: boolean }

const clockOf = (ms: number): string =>
  new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

/**
 * Conversations. A notice is the school telling you something; this is two
 * people talking, so it reads as a thread and arrives without a reload — the
 * same live channel the board uses.
 */
export function Conversations({ threads, openWith }: {
  threads: ReturnType<typeof useQuery<{ threads: Thread[]; unread: number }>>;
  /** Somebody named from elsewhere — a student record, say — to open on. */
  openWith?: number | null;
}): JSX.Element {
  const [withId, setWithId] = useState<number | null>(openWith ?? null);
  const contacts = useQuery<{ contacts: Thread['withUser'][] }>('/inbox/contacts');
  const thread = useQuery<{ withUser: Thread['withUser']; messages: ChatMessage[] }>(
    withId ? `/inbox/threads/${withId}` : null,
  );
  const [draft, setDraft] = useState('');
  // The recipient column doubles as a picker: normally the conversations you
  // have, and on demand everyone you could start one with.
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const foot = useRef<HTMLDivElement | null>(null);

  // A message arriving while the thread is open lands in it, and the unread
  // count that opening it clears has to be re-read too.
  useLive('messages', () => { thread.reload(); threads.reload(); });

  // Stay at the newest message, the way a chat does.
  useEffect(() => { foot.current?.scrollIntoView({ block: 'end' }); }, [thread.data]);

  // Open on the conversation last spoken in, or on the only person there is to
  // write to. Landing on an empty pane and an instruction is a worse greeting
  // than landing in the conversation you almost certainly came for.
  const first = threads.data?.threads[0]?.withUser.id ?? contacts.data?.contacts[0]?.id ?? null;
  useEffect(() => {
    if (withId == null && first != null) setWithId(first);
  }, [first, withId]);

  // Arriving from somebody's record opens on them, whatever was open before.
  useEffect(() => { if (openWith != null) setWithId(openWith); }, [openWith]);

  const send = async () => {
    const body = draft.trim();
    if (!body || !withId) return;
    setSending(true);
    try {
      await api.post(`/inbox/threads/${withId}`, { body });
      setDraft('');
      thread.reload();
      threads.reload();
    } finally { setSending(false); }
  };

  // Everyone they may talk to, with any conversation already going shown first.
  const started = threads.data?.threads ?? [];
  const startedIds = new Set(started.map((t) => t.withUser.id));
  const fresh = (contacts.data?.contacts ?? []).filter((c) => !startedIds.has(c.id));

  const term = search.trim().toLowerCase();
  const matches = (name: string) => !term || name.toLowerCase().includes(term);
  const shownThreads = started.filter((t) => matches(t.withUser.name));
  const shownFresh = fresh.filter((c) => matches(c.name));

  return (
    <div className="chat">
      <div className="chat__list">
        <div className="chat__tools">
          <input
            className="input"
            value={search}
            placeholder="Search people"
            aria-label="Search people"
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className={picking ? 'btn btn--icon chat__new chat__new--on' : 'btn btn--icon chat__new'}
            aria-label={picking ? 'Cancel' : 'New message'}
            aria-pressed={picking}
            title={picking ? 'Cancel' : 'New message'}
            onClick={() => { setPicking((v) => !v); setSearch(''); }}
          >
            <ComposeIcon />
          </button>
        </div>
        <div className="chat__people">
        {!picking && shownThreads.length === 0 && !term && (
          <div className="empty" style={{ padding: 18 }}>No conversations yet.</div>
        )}
        {shownThreads.map((t) => (
          <button key={t.withUser.id} className={`chat__person${t.withUser.id === withId ? ' chat__person--on' : ''}`}
            onClick={() => setWithId(t.withUser.id)}>
            <Avatar userId={t.withUser.id} updatedAt={t.withUser.avatarUpdatedAt} initials={t.withUser.initials} size={32} />
            <span className="chat__who">
              <span className="chat__name">{t.withUser.name}</span>
              <span className="chat__preview">{t.last.mine ? 'You: ' : ''}{t.last.body}</span>
            </span>
            {t.unread > 0 && <span className="chat__badge">{t.unread}</span>}
          </button>
        ))}
        {(picking || term) && shownFresh.length > 0 && <div className="chat__heading">Start a conversation</div>}
        {(picking || term) && shownFresh.map((c) => (
          <button key={c.id} className={`chat__person${c.id === withId ? ' chat__person--on' : ''}`}
            onClick={() => { setWithId(c.id); setPicking(false); setSearch(''); }}>
            <Avatar userId={c.id} updatedAt={c.avatarUpdatedAt} initials={c.initials} size={32} />
            <span className="chat__who">
              <span className="chat__name">{c.name}</span>
              <span className="chat__preview">{c.role}</span>
            </span>
          </button>
        ))}
        {term && !shownThreads.length && !shownFresh.length && (
          <div className="empty" style={{ padding: 18 }}>Nobody by that name.</div>
        )}
        </div>
      </div>

      <div className="chat__thread">
        {!withId && <div className="empty">Pick somebody to write to.</div>}
        {withId && (
          <>
            {thread.data && (
              <div className="chat__with">
                <Avatar
                  userId={thread.data.withUser.id}
                  updatedAt={thread.data.withUser.avatarUpdatedAt}
                  initials={thread.data.withUser.initials}
                  size={26}
                />
                <span>
                  <strong>{thread.data.withUser.name}</strong>
                  <span className="muted"> · {thread.data.withUser.role}</span>
                </span>
              </div>
            )}
            <div className="chat__scroll">
              <div className="chat__stream">
                {thread.data?.messages.length === 0 && (
                  <div className="empty">No messages yet. Say something.</div>
                )}
                {thread.data?.messages.map((m) => (
                  <div key={m.id} className={`bubble${m.mine ? ' bubble--mine' : ''}`}>
                    <span className="bubble__body">{m.body}</span>
                    <span className="bubble__at">{clockOf(m.at)}</span>
                  </div>
                ))}
                <div ref={foot} />
              </div>
            </div>
            <form className="chat__compose" onSubmit={(e) => { e.preventDefault(); void send(); }}>
              <input
                className="input"
                value={draft}
                placeholder={`Message ${thread.data?.withUser.name ?? ''}`}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button className="btn btn--primary" disabled={sending || !draft.trim()}>Send</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
